/**
 * Monitored-event review charts (OMON-201) — built on the SAME Chart.js stack
 * as the developmental-goal trajectory chart, via the shared chart-kit:
 * singleton hover tooltip with carer notes, note-vs-no-note dot colour, and
 * ctrl-wheel / pinch zoom + shift-drag pan. Replaces the first-cut inline-SVG
 * swimlanes (monitored-swimlane.ts).
 *
 * Data: state.patientNarrative — every monitored-event recording already
 * carries `note` + `performerName` (loaders/patients.ts), so the hover/colour
 * data is present; nothing added to the data model.
 *
 * Windowed to oldest-recording..today, shared across every monitored chart on
 * the plan (currentMonitoredWindow). Dispatch on recordingKind:
 *   - rating       → value-over-time line (data-driven y-scale)
 *   - attainment-5 → 1–5 line (like dev goals)
 *   - boolean      → 0/1 stepped line (No / Yes)
 *   - tally        → BOTH layers: a day-bucketed count line + target/escalate
 *                    threshold (the clinical "per-shift" read), AND an
 *                    occurrence "rug" below the axis — one dot per recording at
 *                    its real time, so each carer note is hoverable and you can
 *                    zoom down to an individual occurrence.
 *
 * Per-point metadata travels on the point object (`{x, y, _meta}`); Chart.js
 * preserves `.raw`, so the shared tooltip reads `_meta` generically.
 */

import type { ChartConfiguration } from "chart.js";
import { state } from "../state";
import type { MonitoredEventInfo, NarrativeEntry } from "../types";
import { escapeHtml } from "../utils/html";
import { localTimeOf } from "../utils/time";
import { notApplicableLabel } from "../clients/not-applicable";
import {
  Chart,
  CHART_COLORS,
  pointColor,
  renderExternalTooltip,
  zoomConfig,
  clearSyncGroup,
  registerForSync,
  wireZoomControls,
  thresholdPlugin,
  type ThresholdLine,
} from "./chart-kit";
import {
  windowForRecordings,
  latestMonthView,
  windowedRecordings,
  plottableRecordings,
  thresholdFor,
  countsByDay,
  noteDaysSet,
  noteCountForDay,
  formatWhen,
  type Window,
  isBreach,
  type Threshold,
  type ChartPoint,
  isDiary,
  timelineCaption,
} from "./monitored-chart-data";

// Re-exported so callers keep importing chart concerns from one module.
export { isDiary };

// ── tooltip ──────────────────────────────────────────────────────────────

function buildTooltip(factTitle: string, tooltip: { dataPoints?: Array<{ raw?: unknown }> }): string {
  const item = tooltip.dataPoints?.[0];
  const meta = (item?.raw as ChartPoint | undefined)?._meta;
  if (!meta) return "";
  let html = `<div class="tt-title">${escapeHtml(factTitle)}</div>`;
  const valueLabel = meta.kind === "count" ? "Count" : meta.kind === "bool" ? "State" : meta.kind === "value" ? "Reading" : "Recorded";
  html += `<div class="tt-row"><span class="tt-label">${valueLabel}:</span> ${escapeHtml(meta.label)}</div>`;
  if (meta.kind === "count" && meta.noteCount) {
    html += `<div class="tt-row"><span class="tt-label">Notes:</span> ${meta.noteCount} that day</div>`;
  }
  if (meta.performerName) {
    html += `<div class="tt-row"><span class="tt-label">Carer:</span> ${escapeHtml(meta.performerName)}</div>`;
  }
  html += `<div class="tt-row"><span class="tt-label">When:</span> ${escapeHtml(formatWhen(meta.when))}</div>`;
  if (meta.note) html += `<div class="tt-note">${escapeHtml(meta.note)}</div>`;
  return html;
}

// ── dataset builders per regime ───────────────────────────────────────────

interface BuiltChart {
  datasets: unknown[];
  yMin: number;
  yMax: number;
  yTick: (v: number | string) => string;
  thresholds: ThresholdLine[];
  stepped?: boolean;
}

function numericLine(points: ChartPoint[], thr: Threshold | null): { dataset: Record<string, unknown>; colors: string[] } {
  const colors = points.map((p) => (isBreach(p.y, thr) ? CHART_COLORS.breach : pointColor(!!p._meta.note)));
  return {
    dataset: {
      data: points,
      borderColor: CHART_COLORS.planned,
      borderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 7,
      pointBackgroundColor: colors,
      pointBorderColor: colors,
      tension: 0,
      spanGaps: true,
      order: 1,
    },
    colors,
  };
}

function buildRating(recs: NarrativeEntry[], thr: Threshold | null): BuiltChart {
  const points: ChartPoint[] = recs
    .filter((r) => typeof r.value === "number")
    .map((r) => ({ x: new Date(r.date).getTime(), y: r.value as number,
      _meta: { kind: "value", label: `${r.value}`, note: r.note, performerName: r.performerName, when: r.date } }));
  const ys = points.map((p) => p.y);
  const lo = Math.min(...ys, thr?.value ?? Infinity);
  const hi = Math.max(...ys, thr?.value ?? -Infinity);
  const pad = Math.max(1, Math.round((hi - lo) * 0.15));
  return {
    datasets: [numericLine(points, thr).dataset],
    yMin: lo - pad, yMax: hi + pad,
    yTick: (v) => `${v}`,
    thresholds: thr ? [{ value: thr.value, label: thr.kind === "escalate" ? `escalate > ${thr.value}` : `target ≤ ${thr.value}` }] : [],
  };
}

function buildAttainment(recs: NarrativeEntry[], thr: Threshold | null): BuiltChart {
  const points: ChartPoint[] = recs
    .map((r) => ({ v: typeof r.score === "number" ? r.score : r.value, r }))
    .filter((x): x is { v: number; r: NarrativeEntry } => typeof x.v === "number")
    .map(({ v, r }) => ({ x: new Date(r.date).getTime(), y: v,
      _meta: { kind: "value", label: `${v} / 5`, note: r.note, performerName: r.performerName, when: r.date } }));
  return {
    datasets: [numericLine(points, thr).dataset],
    yMin: 0.5, yMax: 5.5,
    yTick: (v) => (typeof v === "number" && v >= 1 && v <= 5 ? `${v}` : ""),
    thresholds: thr ? [{ value: thr.value, label: thr.kind === "escalate" ? `escalate > ${thr.value}` : `target ≤ ${thr.value}` }] : [],
  };
}

function buildBoolean(recs: NarrativeEntry[]): BuiltChart {
  const points: ChartPoint[] = recs
    .filter((r) => r.state === "yes" || r.state === "no")
    .map((r) => ({ x: new Date(r.date).getTime(), y: r.state === "yes" ? 1 : 0,
      _meta: { kind: "bool", label: r.state === "yes" ? "Yes" : "No", note: r.note, performerName: r.performerName, when: r.date } }));
  const colors = points.map((p) => pointColor(!!p._meta.note));
  return {
    datasets: [{
      data: points,
      borderColor: CHART_COLORS.planned,
      borderWidth: 2,
      pointRadius: 5,
      pointHoverRadius: 8,
      pointBackgroundColor: colors,
      pointBorderColor: colors,
      stepped: true,
      spanGaps: true,
      order: 1,
    }],
    yMin: -0.2, yMax: 1.2,
    yTick: (v) => (v === 1 ? "Yes" : v === 0 ? "No" : ""),
    thresholds: [],
    stepped: true,
  };
}

function buildTally(win: Window, recs: NarrativeEntry[], thr: Threshold | null): BuiltChart {
  const counts = countsByDay(win, recs);
  const noteDays = noteDaysSet(recs);
  const yMax = Math.max(thr?.value ?? 0, ...counts) + 1;
  const rugY = -(yMax * 0.08); // a thin lane below the axis for the occurrence rug

  // Layer 1: day-bucketed count line (one point per window day, incl. zeros).
  const countPoints: ChartPoint[] = win.days.map((day, i) => {
    const noteCount = noteCountForDay(recs, day);
    return {
      x: new Date(`${day}T12:00:00`).getTime(),
      y: counts[i],
      _meta: { kind: "count", label: `${counts[i]} occurrence${counts[i] === 1 ? "" : "s"}`, when: day, noteCount },
    };
  });
  const countColors = countPoints.map((p) =>
    isBreach(p.y, thr) ? CHART_COLORS.breach : (noteDays.has(p._meta.when) ? CHART_COLORS.actualNote : CHART_COLORS.planned));

  // Layer 2: occurrence rug — one dot per recording at its real time.
  const rugPoints: ChartPoint[] = recs.map((r) => ({
    x: new Date(r.date).getTime(),
    y: rugY,
    _meta: { kind: "occurrence", label: "1 occurrence", note: r.note, performerName: r.performerName, when: r.date },
  }));
  const rugColors = rugPoints.map((p) => pointColor(!!p._meta.note));

  return {
    datasets: [
      {
        data: countPoints,
        borderColor: CHART_COLORS.planned,
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 7,
        pointBackgroundColor: countColors,
        pointBorderColor: countColors,
        tension: 0,
        order: 1,
      },
      {
        data: rugPoints,
        showLine: false,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointStyle: "rectRot",
        pointBackgroundColor: rugColors,
        pointBorderColor: rugColors,
        order: 2,
      },
    ],
    yMin: rugY * 1.6, yMax,
    yTick: (v) => (typeof v === "number" && v >= 0 && Number.isInteger(v) ? `${v}` : ""),
    thresholds: thr ? [{ value: thr.value, label: thr.kind === "escalate" ? `escalate > ${thr.value}` : `target ≤ ${thr.value}` }] : [],
  };
}

function buildForRegime(m: MonitoredEventInfo, recs: NarrativeEntry[], win: Window): BuiltChart {
  const thr = thresholdFor(m);
  switch (m.recordingKind) {
    case "rating": return buildRating(recs, thr);
    case "attainment-5": return buildAttainment(recs, thr);
    case "boolean": return buildBoolean(recs);
    case "tally":
    default: return buildTally(win, recs, thr);
  }
}

// ── render + hydrate ───────────────────────────────────────────────────────

function emptyNote(): string {
  return `<div style="margin-top:8px;padding:8px 12px;background:#f9fafb;border:1px dashed #e5e7eb;border-radius:8px;font-size:13px;color:#6b7280;">No recordings on this plan yet.</div>`;
}

// OMON-308: "all recordings to date" described the axis, and the axis is now the
// latest month. The history is still there — it is a zoom-out away, which the
// caption has to say or the earlier recordings look deleted.
const captionFor: Record<string, string> = {
  rating: `Reading over time · latest month · ctrl+scroll or pinch to zoom out for the full history, hover a point for the note.`,
  boolean: `Yes / no per shift · latest month · zoom out for the full history, hover a point for the note.`,
  "attainment-5": `Support scale (0–5) · latest month · zoom out for the full history, hover a point for the note.`,
  tally: `Count per shift + every occurrence (rug) · latest month · zoom out for the full history, hover an occurrence for its note.`,
};

// ── OMON-204: diary timeline (not a chart) ──────────────────────────────────

function diaryDayHeading(day: string): string {
  const d = new Date(`${day}T12:00:00`);
  if (isNaN(d.getTime())) return day;
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

function diaryTime(iso: string): string {
  // Shares utils/time with the Combined narrative — the two tabs render the
  // same recordings, and when each owned its formatter they disagreed by an
  // hour through BST. Keeps its own blank fallback: an unreadable date shows
  // nothing here rather than an em dash.
  return localTimeOf(iso, "");
}

/**
 * Every diary recording for one trackable, across the WHOLE
 * plan — not the trailing review window monitoredChartHTML uses. The diary is
 * read as a running record, so windowing it would silently hide history.
 */
export function diaryTimelineAll(odCode: string, narrative: NarrativeEntry[], recordingKind?: string): string {
  const recs = narrative
    .filter((e) => e.code === odCode)
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date));
  if (recs.length === 0) return emptyNote();
  return diaryTimelineHTML(recs, recordingKind);
}

/**
 * The diary regime renders as a per-day TIMELINE, not a chart — chronological
 * timestamped free-text entries (oldest first, matching the Combined-narrative
 * convention). Reads the same note-bearing narrative entries, filtered to the
 * fact + window. Returns plain HTML (no canvas), so createMonitoredCharts skips it.
 */
function diaryTimelineHTML(recs: NarrativeEntry[], recordingKind?: string): string {
  // ADR-0035 am.9b: a not-applicable answer is part of the item's history
  // (with or without a note), shown muted as "Not applicable — {reason}".
  const noted = recs.filter((r) => r.kind === "not-applicable" || (r.note ?? "").trim());
  if (noted.length === 0) return emptyNote();
  const byDay = new Map<string, NarrativeEntry[]>();
  for (const r of noted) {
    const day = r.date.slice(0, 10);
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day)!.push(r);
  }
  let html = `<div style="font-size:11px;color:#6b7280;margin-top:8px;">${timelineCaption(recordingKind)}</div>`;
  // Newest day first, but entries within a day stay in time order so a day
  // reads morning-to-evening — same rule as the combined narrative, so the two
  // transcripts read the same way round.
  for (const [day, entries] of [...byDay].reverse()) {
    html += `<div style="font-size:11px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#991b1b;margin:12px 0 4px;">${escapeHtml(diaryDayHeading(day))}</div>`;
    for (const e of entries) {
      if (e.kind === "not-applicable") {
        html += `
        <div class="diary-na" style="display:flex;gap:8px;padding:6px 4px;border-top:1px solid #f3f4f6;">
          <div style="width:46px;flex:0 0 auto;font-size:13px;color:#9ca3af;font-variant-numeric:tabular-nums;">${escapeHtml(diaryTime(e.date))}</div>
          <div style="flex:1;font-size:13px;color:#6b7280;font-style:italic;line-height:1.5;">${escapeHtml(notApplicableLabel(e.notApplicableText))}${e.note?.trim()
            ? `<div style="white-space:pre-wrap;margin-top:2px;">${escapeHtml(e.note)}</div>` : ""}
            ${e.performerName ? `<div style="font-size:11px;color:#9ca3af;margin-top:2px;font-style:normal;">${escapeHtml(e.performerName)}</div>` : ""}
          </div>
        </div>`;
        continue;
      }
      html += `
        <div style="display:flex;gap:8px;padding:6px 4px;border-top:1px solid #f3f4f6;">
          <div style="width:46px;flex:0 0 auto;font-size:13px;color:#6b7280;font-variant-numeric:tabular-nums;">${escapeHtml(diaryTime(e.date))}</div>
          <div style="flex:1;">
            <div style="font-size:13px;color:#374151;line-height:1.5;white-space:pre-wrap;">${escapeHtml(e.note ?? "")}</div>
            ${e.performerName ? `<div style="font-size:11px;color:#9ca3af;margin-top:2px;">${escapeHtml(e.performerName)}</div>` : ""}
          </div>
        </div>`;
    }
  }
  return html;
}

/**
 * The chart area for one monitored-event card: an empty note when there are no
 * in-window recordings; a per-day timeline for the `diary` regime; otherwise a
 * caption + a canvas tagged with the fact code for createMonitoredCharts to hydrate.
 */
/**
 * The window EVERY monitored-event chart on this plan shares: oldest recording
 * across all of them (diary excluded — it has its own tab and renders a
 * timeline, not a chart) through today. Computed the same way by the HTML
 * renderer and the Chart builder so the axis and the zoom limits agree.
 */
export function currentMonitoredWindow(): Window {
  const codes = state.patientMonitoredEvents.filter((e) => !isDiary(e)).map((e) => e.odCode);
  return windowForRecordings(state.patientNarrative, codes);
}

export function monitoredChartHTML(m: MonitoredEventInfo, narrative: NarrativeEntry[]): string {
  const win = currentMonitoredWindow();
  const all = windowedRecordings(narrative, m.odCode, win);
  if (all.length === 0) return emptyNote();
  // OMON-204: diary is a timeline, not a chart; so is an incident (OMON-232).
  if (isDiary(m)) return diaryTimelineHTML(all, m.recordingKind);
  // ADR-0035 am.9b: not-applicable answers never plot. A trackable answered
  // ONLY that way says so instead of drawing an empty (or zero) chart.
  const recs = plottableRecordings(all);
  if (recs.length === 0) {
    const n = all.length;
    return `<div style="margin-top:8px;padding:8px 12px;background:#f9fafb;border:1px dashed #e5e7eb;border-radius:8px;font-size:13px;color:#6b7280;">No readings on this plan yet · marked not applicable on ${n} shift${n === 1 ? "" : "s"}.</div>`;
  }
  const caption = captionFor[m.recordingKind || "tally"] || captionFor.tally;
  return `
    <div style="font-size:11px;color:#6b7280;margin-top:8px;">${caption}</div>
    <div style="position:relative;height:150px;margin-top:6px;">
      <canvas data-me-code="${escapeHtml(m.odCode)}"></canvas>
    </div>`;
}

/** Build one Chart on a canvas for a monitored event. Pushed to state.activeCharts. */
function buildMonitoredChart(canvas: HTMLCanvasElement, m: MonitoredEventInfo, narrative: NarrativeEntry[]): Chart {
  const win = currentMonitoredWindow();
  // ADR-0035 am.9b: not-applicable answers are never plotted.
  const recs = plottableRecordings(windowedRecordings(narrative, m.odCode, win));
  const built = buildForRegime(m, recs, win);

  // OMON-308: open on the latest month with today at the right edge (Connect's
  // live-tail shape). `win` stays the FULL span — it drives day-bucketing and
  // the pan/zoom limits below, so the older history is scrolled past, not lost.
  const view = latestMonthView(win, recs.map((r) => new Date(r.date).getTime()).filter(Number.isFinite));

  const config = {
    type: "line" as const,
    data: { datasets: built.datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 300 },
      layout: { padding: { left: 4, right: 8, top: 6, bottom: 0 } },
      scales: {
        x: {
          type: "time" as const,
          time: {
            minUnit: "hour" as const,
            displayFormats: { week: "d MMM", day: "d MMM", hour: "HH:mm" },
            tooltipFormat: "d MMM yyyy, HH:mm",
          },
          min: view.min,
          max: view.max,
          grid: { display: false },
          ticks: { font: { size: 9 }, color: "#9ca3af", maxRotation: 0, autoSkip: true, autoSkipPadding: 12 },
        },
        y: {
          min: built.yMin,
          max: built.yMax,
          ticks: {
            font: { size: 9 },
            color: "#9ca3af",
            callback: (val: string | number) => built.yTick(val),
          },
          grid: { color: CHART_COLORS.grid },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          mode: "nearest" as const,
          intersect: false,
          external: (context: unknown) =>
            renderExternalTooltip(context as Parameters<typeof renderExternalTooltip>[0], (tt) => buildTooltip(m.title, tt)),
        },
        zoom: zoomConfig(win.startMs, win.endMs, "monitored"),
      },
      interaction: { mode: "nearest" as const, intersect: false, axis: "x" as const },
    },
    plugins: built.thresholds.length ? [thresholdPlugin(built.thresholds)] : [],
  };

  return new Chart(canvas, config as unknown as ChartConfiguration);
}

/** Hydrate every monitored-event canvas in the DOM from current state. */
export function createMonitoredCharts(): void {
  clearSyncGroup("monitored");
  document.querySelectorAll<HTMLCanvasElement>("[data-me-code]").forEach((canvas) => {
    const code = canvas.getAttribute("data-me-code")!;
    const m = state.patientMonitoredEvents.find((e) => e.odCode === code);
    if (!m) return;
    const chart = buildMonitoredChart(canvas, m, state.patientNarrative);
    state.activeCharts.push(chart);
    const x = chart.options?.scales?.x as { min?: number; max?: number } | undefined;
    registerForSync("monitored", chart, Number(x?.min ?? 0), Number(x?.max ?? 0));
  });

  wireZoomControls("monitored", "monitoredRange", "monitoredResetZoom");
}
